import React, {PropsWithChildren, useCallback, useMemo, useRef, useState} from 'react';
import Nav from "react-bootstrap/Nav";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp
} from "react-bootstrap-icons";
import ProfilePicture from "../../ProfilePicture";
import useAuth from "../../hooks/useAuth";
import {useCloseOutsideClick, UserNameSpan} from "fearless-common-ui";
import usePreference from "../../hooks/usePreference";
import IconSpan from "./IconSpan";
import RenderOption from "./RenderOption";
import sideBarMenuItems from "./sidebarMenuItems";
import styles from './MenuSidebar.module.scss';

const paddingY = 'py-2';
const linkStyles = `d-inline-flex align-items-center text-decoration-none fw-semibold neutral-700`;
const subLinkStyles = `bg-neutral-200 d-block ${paddingY} pe-4 text-decoration-none neutral-700`;

/**
 * Custom dropdown menu for the menu sidebar.
 */
function CustomDropdown({ title, collapsed, children }: PropsWithChildren<{
    title: React.JSX.Element;
    collapsed: boolean;
}>) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const callback = useCallback(() => {
        if (collapsed) {
            setOpen(false);
        }
    }, [collapsed, setOpen]);

    useCloseOutsideClick({ref, callback});

    return (
      <div className="position-relative">
          <div
            onClick={(e) => {
                setOpen(!open);
            }}
            onBlur={collapsed ? () => setOpen(false) : undefined}
            className="d-block cursor-pointer hover-bg position-relative"
            ref={ref}
          >
              <span>{title}</span>
              <span
                className={`${collapsed ? 'px-0' : 'px-3'} ${paddingY} position-absolute`}
                style={{ top: 0, right: collapsed ? 10: 15 }}
              >
                    {collapsed ? (open ? <ChevronLeft /> : <ChevronRight />) : (open ? <ChevronUp /> : <ChevronDown />)}
                </span>
          </div>
          {open && (
            <div
              className={collapsed ? 'position-absolute start-100 top-0 shadow-lg border-end border-top border-bottom border-default' : undefined}
              style={{ width: collapsed ? 300: 'auto', zIndex: 400 }}
            >
                {children}
            </div>
          )}
      </div>
    )
}

/**
 * User menu sidebar after logging in.
 */
export default function MenuSidebar({ children }: PropsWithChildren<{}>) {
    const { preferenceValue, setPreferenceValue } = usePreference('logged-in-user-side-menu');
    const collapsed = useMemo(() => {
        return preferenceValue === '1';
    }, [preferenceValue]);
    const setCollapsed = (bool: boolean) => {
        setPreferenceValue(bool ? '1' : '0');
    }
    const { user } = useAuth();

    const options = useMemo(() => {
        return user ? sideBarMenuItems({ user }) : [];
    }, [user]);

    const width = collapsed ? 90 : 300;

    return (
      <Row>
        <Nav
          className={`col bg-neutral-100 p-0 align-items-stretch border-end border-default d-none d-sm-flex ${styles.MenuSidebar}`}
          style={{flex: `0 0 ${width}px` }}
          activeKey="/home"
          as={Col}
        >
          <button className={styles.ToggleHover} onClick={() => setCollapsed(!collapsed)}>&nbsp;</button>
          <div className="d-flex flex-column align-items-stretch p-0 gap-0 flex-grow-1">
              <Nav.Item className={`${linkStyles} ${paddingY} ps-4`}>
                  <div
                    className="d-flex cursor-pointer align-items-center"
                    onClick={() => setCollapsed(!collapsed)}
                  >
                      {!collapsed && user && (
                        <>
                            <ProfilePicture user={user} size={60} />
                            <div className="text-truncate" style={{ flex: '0 1 160px'}}>
                                <UserNameSpan user={user} />
                                <small className="neutral-600 fw-normal">{user.email}</small>
                            </div>
                        </>
                      )}
                      <button className={`bg-transparent border-0 ${collapsed ? 'ps-3 py-0': ''}`}>
                          {collapsed ? <ChevronRight />: <ChevronLeft />}
                      </button>
                  </div>
              </Nav.Item>
              {options.map((o) => {
                  return (
                    <React.Fragment key={o.href}>
                        {o.separatorTop && <hr className="py-1 mb-1"/>}
                        {o.options ? (
                          <CustomDropdown
                            collapsed={collapsed}
                            title={(
                              <Nav.Item
                                as="a"
                                className={`${linkStyles} ${paddingY} ${collapsed ? 'ps-4 pe-0' : 'px-4'}`}
                              >
                                  {o.icon && (
                                    <IconSpan
                                      icon={o.icon}
                                      tooltip={collapsed ? o.label : undefined}
                                      width={collapsed ? 45 : 60}
                                    />
                                  )}
                                  <span className={collapsed ? "d-none" : "d-sm-inline-block"}>
                                                {o.label}
                                            </span>
                              </Nav.Item>
                            )}
                          >
                              {o.options.map((subO) => (
                                <RenderOption
                                  key={subO.href}
                                  className={`${collapsed ? 'ps-3' : 'ps-5'} ${subLinkStyles}`}
                                  option={subO}
                                  collapsed={false}
                                />
                              ))}
                          </CustomDropdown>
                        ) : (
                          <RenderOption
                            option={o}
                            className={`${o.className || ''} ${linkStyles} ${paddingY} px-4`}
                            collapsed={collapsed}
                          />
                        )}
                    </React.Fragment>
                  );
              })}
          </div>
        </Nav>
        <Col className="ms-0 ps-0">
          {children}
        </Col>
      </Row>
    );
}
